import styled from 'styled-components';
import React, {FC, useRef, useEffect} from 'react';
import {navigate} from '@reach/router';

import {paletteR4, fontFamiliesR4} from '../../../../lib/styleVariablesR4';
import {
  mediaBreakpointDown,
  mediaBreakpointOnly,
  fontWeights,
  transition
} from '../../../../lib/styleVariables';
import {headerHeight} from './headerVariables';
import {Icon} from '../../../molecules/icon';
import {convertHexToRGBA} from '../../../../lib/color-utils';
import {useTranslation} from '../../../../context/translation';
import {getLocationUrl} from '../../../molecules/filter';

interface OverlaySearchProps {
  isVisible: boolean;
  topBarHeight: number;
}

const StyledWrapper = styled.div<{isVisible: boolean; topBarHeight: number}>`
  width: 100%;
  height: calc(100vh - ${headerHeight.desktop}px);
  background: ${paletteR4.white.base}
    linear-gradient(to bottom, #fffade, ${convertHexToRGBA('#FFF2C5', 0)} 65%);
  padding: 0 40px ${headerHeight.desktop}px;
  position: fixed;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity ${transition.fast};
  opacity: ${props => (props.isVisible ? 1 : 0)};
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};

  ${mediaBreakpointDown('mobile')} {
    height: ${props => `calc(100vh - ${headerHeight.mobile + props.topBarHeight}px)`};
    padding-bottom: ${headerHeight.mobile}px;
  }

  ${mediaBreakpointOnly('tablet')} {
    height: ${props => `calc(100vh - ${headerHeight.tablet + props.topBarHeight}px)`};
    padding-bottom: ${headerHeight.tablet}px;
  }
`;

const StyledInputWrapper = styled.form`
  width: 100%;
  max-width: 886px;
  position: relative;
`;

const StyledInput = styled.input`
  width: 100%;
  padding-right: 48px;
  border-width: 0 0 2px 0;
  border-color: ${paletteR4.black.base};
  background: transparent;
  font-family: ${fontFamiliesR4.frontPageTitle};
  font-weight: ${fontWeights.medium};
  font-size: 41px; /* Not defined in typography variables */
  line-height: 64px;
  color: ${paletteR4.red.lowlight};

  &::-webkit-input-placeholder {
    color: ${paletteR4.red.lowlight};
  }
  &::-moz-placeholder {
    color: ${paletteR4.red.lowlight};
  }
  &:-ms-input-placeholder {
    color: ${paletteR4.red.lowlight};
  }
  &:-moz-placeholder {
    color: ${paletteR4.red.lowlight};
  }

  &:focus {
    outline: none;
  }

  ${mediaBreakpointDown('mobile')} {
    font-size: 24px;
    line-height: 40px;
    padding-right: 32px;
  }

  ${mediaBreakpointOnly('tablet')} {
    font-size: 32px;
    line-height: 52px;
  }
`;

const StyledIcon = styled(Icon)`
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);

  * {
    fill: none;
  }

  ${mediaBreakpointDown('mobile')} {
    width: 20px;
    height: 20px;
  }
`;

export const OverlaySearch: FC<OverlaySearchProps> = props => {
  const {isVisible, topBarHeight} = props;
  const {t} = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // @ts-ignore
    const inputVal = searchInputRef.current.value;
    const url = getLocationUrl();

    if (!url || inputVal.trim() === '')
      return;

    const urlObj = new URL(url);
    const targetUrl = new URL(`${urlObj.origin}/blog/search`);
    targetUrl.searchParams.set('query', inputVal.trim());
    navigate(targetUrl.toString(), {replace: true});
  };

  useEffect(() => {
    if (isVisible && searchInputRef?.current)
      searchInputRef.current.focus();
  }, [isVisible]);

  return (
    <StyledWrapper isVisible={isVisible} topBarHeight={topBarHeight}>
      <StyledInputWrapper onSubmit={handleSubmit}>
        <StyledInput
          type="text"
          placeholder={t('front-page.home.search-input-placeholder')}
          autoFocus
          ref={searchInputRef}
        />
        <StyledIcon name="magnifyingGlassFrontPage" size={31} useR4 />
      </StyledInputWrapper>
    </StyledWrapper>
  );
};
